import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRealtime } from '../providers/RealtimeProvider';
import { listActives } from '../services/activeService';
import { getCompanyProfile } from '../services/companyService';
import { resolveServiceAreaCenter } from '../utils/serviceArea';
import {
  DEFAULT_FLEET_CENTER,
  DEFAULT_FLEET_ZOOM,
  applyFleetAssignmentEvent,
  applyFleetHeartbeatEvent,
  applyFleetLocationEvent,
  applyFleetPresenceEvent,
  getFleetCounts,
  normalizeActiveFleetRecord,
} from '../utils/liveFleet';

const unwrapActives = (response) => {
  const payload =
    response?.data?.data ||
    response?.data?.actives ||
    response?.data?.results ||
    response?.data ||
    [];
  return Array.isArray(payload) ? payload : [];
};

export default function useLiveFleet() {
  const { socket, connected } = useRealtime();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [freshnessNow, setFreshnessNow] = useState(Date.now());
  const [serviceCenter, setServiceCenter] = useState(DEFAULT_FLEET_CENTER);
  const [serviceZoom, setServiceZoom] = useState(DEFAULT_FLEET_ZOOM);
  const hasConnectedRef = useRef(false);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError('');
    try {
      const response = await listActives();
      const nextDrivers = unwrapActives(response)
        .map((record) => normalizeActiveFleetRecord(record))
        .filter(Boolean);
      setDrivers(nextDrivers);
      setFreshnessNow(Date.now());
      return nextDrivers;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load live fleet data.');
      throw err;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  useEffect(() => {
    let ignore = false;

    const loadServiceArea = async () => {
      try {
        const response = await getCompanyProfile();
        const company = response?.data?.company || response?.data || null;
        const serviceArea = await resolveServiceAreaCenter(company);
        if (ignore || !serviceArea?.center) return;
        setServiceCenter([serviceArea.center.lat, serviceArea.center.lng]);
        setServiceZoom(serviceArea.zoom || DEFAULT_FLEET_ZOOM);
      } catch (_err) {
        // The shared default keeps the map useful when company settings are unavailable.
      }
    };

    loadServiceArea();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setFreshnessNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!connected) return;
    if (hasConnectedRef.current) refresh().catch(() => {});
    else hasConnectedRef.current = true;
  }, [connected, refresh]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleLocation = (payload) => {
      setFreshnessNow(Date.now());
      setDrivers((current) => applyFleetLocationEvent(current, payload));
    };
    const handlePresence = (payload) => {
      setDrivers((current) => applyFleetPresenceEvent(current, payload));
    };
    const handleAssignment = (payload) => {
      setDrivers((current) => applyFleetAssignmentEvent(current, payload));
    };
    const handleHeartbeat = (payload) => {
      setFreshnessNow(Date.now());
      setDrivers((current) => applyFleetHeartbeatEvent(current, payload));
    };

    socket.on('driver:location', handleLocation);
    socket.on('driver:presence', handlePresence);
    socket.on('assignment:updated', handleAssignment);
    socket.on('driver:trip-heartbeat', handleHeartbeat);

    return () => {
      socket.off('driver:location', handleLocation);
      socket.off('driver:presence', handlePresence);
      socket.off('assignment:updated', handleAssignment);
      socket.off('driver:trip-heartbeat', handleHeartbeat);
    };
  }, [socket]);

  const counts = useMemo(() => getFleetCounts(drivers), [drivers]);

  return {
    drivers,
    counts,
    loading,
    refreshing,
    error,
    refresh,
    freshnessNow,
    serviceCenter,
    serviceZoom,
    connected,
  };
}
